import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Coins, Wallet, TrendingUp, Trophy, ShoppingBag,
  Loader2, Gift, Clock, Zap, Star, ArrowDownLeft,
  ArrowUpRight, Sparkles, Crown, Medal, Award, ArrowRight,
  Send, Check, User, Search, ChevronDown, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SIG = {
  check_in:            { icon: Clock,      label: 'Check-in' },
  check_in_on_time:    { icon: Zap,        label: 'On-time bonus' },
  check_out:           { icon: Clock,      label: 'Check-out' },
  task_created:        { icon: Star,       label: 'Task created' },
  task_completed:      { icon: Trophy,     label: 'Task completed' },
  timesheet_submitted: { icon: TrendingUp, label: 'Timesheet' },
  expense_retired:     { icon: Wallet,     label: 'Expense retired' },
  manager_award:       { icon: Gift,       label: 'Manager award' },
  redemption:          { icon: ShoppingBag, label: 'Redemption' },
  day_planner:         { icon: Star,       label: 'Day planner' },
  ping_sent:           { icon: Zap,        label: 'Ping sent' },
  survey_completed:    { icon: Award,      label: 'Survey done' },
  streak_bonus:        { icon: Zap,        label: 'Streak bonus' },
  peer_send:           { icon: Send,       label: 'Sent coins' },
  peer_receive:        { icon: ArrowDownLeft, label: 'Received coins' },
};

const SIG_CAT = {
  check_in: 'Attendance', check_in_on_time: 'Attendance', check_out: 'Attendance',
  task_created: 'Tasks', task_completed: 'Tasks', timesheet_submitted: 'Planning',
  expense_retired: 'Finance', manager_award: 'Recognition', redemption: 'Finance',
  day_planner: 'Planning', ping_sent: 'Collaboration', survey_completed: 'Engagement',
  peer_send: 'Transfer', peer_receive: 'Transfer', streak_bonus: 'Attendance',
};

const CAT_DOT = {
  Attendance: 'bg-sky-500', Tasks: 'bg-emerald-500', Planning: 'bg-indigo-500',
  Finance: 'bg-amber-500', Collaboration: 'bg-violet-500', Engagement: 'bg-pink-500',
  Recognition: 'bg-rose-500', Transfer: 'bg-teal-500',
};

const relTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')} ${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}`;
};

const fmtCur = (n, c) => n != null ? `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}` : '';

/* ── Donut SVG ── */
const MiniDonut = ({ pct, color, size = 52, stroke = 5 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-border/50" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }} />
    </svg>
  );
};

/* ── Confetti particles ── */
const ConfettiParticle = ({ delay, x, color }) => (
  <motion.div
    initial={{ y: 0, x: x, opacity: 1, scale: 1 }}
    animate={{ y: [0, -60, 20], x: [x, x + (Math.random() - 0.5) * 40], opacity: [1, 1, 0], scale: [1, 1.2, 0.6], rotate: [0, 180, 360] }}
    transition={{ duration: 1.2, delay, ease: 'easeOut' }}
    className="absolute rounded-sm"
    style={{ width: 6, height: 6, backgroundColor: color, top: '40%', left: '50%' }}
  />
);


const MyCoins = () => {
  const { getAuthHeaders, user, currentOffice } = useAuth();
  const [tab, setTab] = useState('rewards');
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [rate, setRate] = useState(null);
  const [showCurrency, setShowCurrency] = useState(true);
  const [redeemModal, setRedeemModal] = useState(null);
  const [busy, setBusy] = useState(false);

  // Send coins state
  const [sendStep, setSendStep] = useState('form'); // 'form' | 'confirm' | 'success'
  const [sendOpen, setSendOpen] = useState(false);
  const [teammates, setTeammates] = useState([]);
  const [teammatesLoading, setTeammatesLoading] = useState(false);
  const [sendTo, setSendTo] = useState(null); // {user_id, name}
  const [sendAmount, setSendAmount] = useState('');
  const [sendMsg, setSendMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [showRecipientList, setShowRecipientList] = useState(false);

  const rewardsRef = useRef(null);
  const cur = currentOffice?.currency || 'TZS';
  const toC = (c) => showCurrency && rate ? c / rate : null;
  const curDisplay = (c) => showCurrency && rate ? fmtCur(c / rate, cur) : (rate ? `***** ${cur}` : '');

  const freshHeaders = () => getAuthHeaders();

  const load = useCallback(async () => {
    setLoading(true);
    const hdrs = freshHeaders();
    const f = (u) => fetch(`${API}${u}`, { headers: hdrs }).then(r => r.ok ? r.json() : null).catch(() => null);
    const [w, r, l, c, cfg] = await Promise.all([
      f('/api/ecocoins/my-wallet'), f('/api/ecocoins/marketplace'),
      f('/api/ecocoins/leaderboard'), f('/api/ecocoins/config/conversions'),
      f('/api/ecocoins/config'),
    ]);
    if (w) setWallet(w);
    if (r) setRewards(r.rewards || []);
    if (l) setLeaderboard(l.leaderboard || []);
    if (c) { const cv = c.conversions?.find(x => x.office_id === currentOffice?.id); if (cv) setRate(cv.coins_per_unit); }
    if (cfg) setShowCurrency(cfg.show_currency_balance !== false);
    setLoading(false);
  }, [currentOffice?.id]);

  const loadTeammates = useCallback(async () => {
    setTeammatesLoading(true);
    try {
      const res = await fetch(`${API}/api/ecocoins/teammates`, { headers: freshHeaders() });
      if (res.ok) { const d = await res.json(); setTeammates(d.teammates || []); }
    } catch {}
    finally { setTeammatesLoading(false); }
  }, []);

  useEffect(() => { load(); }, []);

  const openSendDialog = () => {
    setSendStep('form');
    setSendTo(null);
    setSendAmount('');
    setSendMsg('');
    setRecipientSearch('');
    setSendResult(null);
    setSendOpen(true);
    loadTeammates();
  };

  const handleSend = async () => {
    if (!sendTo || !sendAmount || parseInt(sendAmount) <= 0) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/ecocoins/send`, {
        method: 'POST', headers: { ...freshHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient_user_id: sendTo.user_id, coins: parseInt(sendAmount), message: sendMsg }),
      });
      const d = await res.json();
      if (res.ok && d.success) {
        setSendResult({ ...d, recipient: sendTo.name, coins: parseInt(sendAmount), message: sendMsg });
        setSendStep('success');
        load();
      } else {
        toast.error(d.detail || 'Failed to send');
      }
    } catch { toast.error('Network error'); }
    finally { setSending(false); }
  };

  const openRedeem = (rw) => {
    if ((wallet?.balance || 0) >= rw.coins_cost) {
      setRedeemModal(rw);
    }
  };

  const handleRedeem = async (rw) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/ecocoins/redeem`, {
        method: 'POST', headers: { ...freshHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reward_id: rw.id, coins: rw.coins_cost, description: rw.name }),
      });
      const d = await res.json();
      if (res.ok && d.success) { toast.success(d.message); setRedeemModal(null); load(); }
      else toast.error(d.detail || 'Failed');
    } catch { toast.error('Network error'); }
    finally { setBusy(false); }
  };

  const scrollToRewards = () => {
    setTab('rewards');
    setTimeout(() => rewardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96" data-testid="mycoins-loading">
      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
    </div>
  );

  const bal = wallet?.balance || 0;
  const earned = wallet?.total_earned || 0;
  const spent = wallet?.total_redeemed || 0;
  const txns = wallet?.recent_transactions || [];
  const cats = [...new Set(rewards.map(r => r.category))];

  const earnedByCategory = {};
  txns.filter(t => t.coins > 0).forEach(t => {
    const cat = t.signal?.includes('task') ? 'Tasks'
      : t.signal?.includes('check') ? 'Attendance'
      : t.signal?.includes('timesheet') ? 'Planning'
      : t.signal?.includes('expense') ? 'Finance'
      : 'Other';
    earnedByCategory[cat] = (earnedByCategory[cat] || 0) + t.coins;
  });
  const catEntries = Object.entries(earnedByCategory);
  const catColors = { Tasks: '#059669', Attendance: '#0779BC', Planning: '#7c3aed', Finance: '#d97706', Other: '#6b7280' };

  const filteredTeammates = teammates.filter(t =>
    t.name.toLowerCase().includes(recipientSearch.toLowerCase())
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6 space-y-6" data-testid="mycoins-page">

      {/* ═══ TOP ROW ═══ */}
      <div className="grid grid-cols-12 gap-6 items-stretch" style={{ minHeight: 240 }}>
        {/* ── Balance Card ── */}
        <div className="col-span-12 lg:col-span-5">
          <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
            className="relative rounded-[20px] overflow-hidden"
            style={{
              background: 'linear-gradient(145deg, #2196e3 0%, #0a7abf 25%, #0668a3 50%, #044d7a 75%, #033756 100%)',
              padding: '32px 28px 28px',
              boxShadow: '0 25px 50px -12px rgba(7, 121, 188, 0.5), 0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.08)',
              transform: 'perspective(900px) rotateY(-2deg)', minHeight: 240,
            }}
            data-testid="wallet-card"
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.06) 45%, rgba(255,255,255,0.02) 55%, transparent 70%)' }} />
            <div className="absolute -top-8 -left-8 w-48 h-48 opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(200,230,255,0.7) 0%, transparent 60%)' }} />
            <div className="absolute -bottom-12 -right-12 w-40 h-40 opacity-10 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 60%)' }} />

            <div className="relative flex items-start justify-between">
              <p className="text-white/70 text-[11px] font-semibold tracking-[0.15em] uppercase">Balance details</p>
              <span className="text-white/60 text-sm font-extrabold tracking-[0.35em] uppercase" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>ECOCOINS</span>
            </div>

            <div className="relative mt-5">
              <motion.p key={bal} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="text-white text-[44px] font-bold leading-none tracking-tight"
                style={{ fontVariantNumeric: 'tabular-nums', textShadow: '0 2px 4px rgba(0,0,0,0.15)' }}
              >{bal.toLocaleString()}</motion.p>
              <p className="text-white/60 text-sm mt-1.5 font-semibold tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {curDisplay(bal)}
              </p>
            </div>

            <div className="relative mt-8 flex items-end justify-between">
              <div>
                <p className="text-white/45 text-[9px] font-medium tracking-[0.15em] uppercase mb-1">Account number</p>
                <p className="text-white/75 text-xs font-mono tracking-[0.14em]" style={{ textShadow: '0 1px 1px rgba(0,0,0,0.15)' }}>
                  {(() => {
                    const uid = user?.id || '';
                    const hash = uid.split('').reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
                    const suffix = String(Math.abs(hash) % 10000000).padStart(7, '0');
                    return `135000${suffix}`;
                  })()}
                </p>
              </div>
              <div className="w-12 h-9 rounded-md overflow-hidden flex-shrink-0" style={{
                background: 'linear-gradient(145deg, #ecd174 0%, #d4a726 35%, #c49520 60%, #b8891a 100%)',
                boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.45), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.2)',
              }}>
                <div className="w-full h-full relative">
                  <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(90deg, transparent 0px, transparent 3.5px, rgba(0,0,0,0.06) 3.5px, rgba(0,0,0,0.06) 4.5px)' }} />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-4 rounded-sm" style={{
                    background: 'linear-gradient(135deg, #e8c547 0%, #d9b432 100%)',
                    boxShadow: 'inset 0 0.5px 1px rgba(255,255,255,0.5), inset 0 -0.5px 1px rgba(0,0,0,0.15)',
                  }}>
                    <div className="absolute top-1/2 left-0 right-0 h-px bg-black/10" />
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-black/10" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Breakdown + Actions ── */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-card border border-border rounded-[20px] shadow-sm p-6 h-full flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Earned</p>
                <p className="text-2xl font-bold text-foreground mt-0.5 tabular-nums">+{earned.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">{curDisplay(earned)}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <div className="h-px bg-border my-3" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Redeemed</p>
                <p className="text-2xl font-bold text-foreground mt-0.5 tabular-nums">-{spent.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">{curDisplay(spent)}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-rose-500" />
              </div>
            </div>
            <div className="h-px bg-border my-3" />
            <div className="flex items-center gap-3">
              <Button size="sm" className="flex-1 h-9 text-xs rounded-lg" style={{ backgroundColor: '#0779BC' }}
                onClick={scrollToRewards} data-testid="redeem-action-btn">
                <ShoppingBag className="w-3.5 h-3.5 mr-1.5" /> Redeem
              </Button>
              <Button size="sm" variant="outline" className="flex-1 h-9 text-xs rounded-lg"
                onClick={openSendDialog} data-testid="send-coins-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Send
              </Button>
            </div>
          </div>
        </div>

        {/* ── Earning Breakdown ── */}
        <div className="col-span-12 lg:col-span-3">
          <div className="bg-card border border-border rounded-[20px] shadow-sm p-5 h-full flex flex-col">
            <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-4 flex-shrink-0">Earning Breakdown</p>
            {catEntries.length === 0 ? (
              <div className="flex items-center justify-center flex-1 text-muted-foreground"><p className="text-xs">No data yet</p></div>
            ) : (
              <div className="space-y-4 overflow-y-auto flex-1" style={{ maxHeight: 190 }}>
                {catEntries.map(([cat, coins]) => {
                  const pct = earned > 0 ? Math.round((coins / earned) * 100) : 0;
                  return (
                    <div key={cat} className="flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        <MiniDonut pct={pct} color={catColors[cat] || '#6b7280'} size={42} stroke={4} />
                        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-foreground">{pct}%</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground">{coins} coins</p>
                        <p className="text-[10px] text-muted-foreground">{cat}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ BOTTOM ROW ═══ */}
      <div className="grid grid-cols-12 gap-6">
        {/* LEFT — Transactions */}
        <div className="col-span-12 lg:col-span-7 bg-card border border-border rounded-[20px] shadow-sm" data-testid="transactions-panel">
          <div className="px-6 py-4 flex items-center justify-between border-b border-border">
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Transactions History</span>
            <button className="text-xs font-medium flex items-center gap-1 hover:underline" style={{ color: '#0779BC' }}>See all <ArrowRight className="w-3 h-3" /></button>
          </div>
          {txns.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-muted-foreground">
              <Wallet className="w-7 h-7 mb-2 opacity-40" /><p className="text-sm">No transactions yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {txns.slice(0, 6).map((t, i) => {
                const s = SIG[t.signal] || { icon: Coins, label: t.signal };
                const Ic = s.icon;
                const pos = t.coins > 0;
                const cat = SIG_CAT[t.signal] || 'General';
                return (
                  <div key={i} className="flex items-center gap-4 px-6 py-4 hover:bg-accent/30 transition-colors" data-testid={`transaction-row-${i}`}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0779BC' }}>
                      <Ic className="w-4 h-4 text-white/80" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{t.description || s.label}</p>
                      <p className="text-[11px] text-muted-foreground">{relTime(t.created_at)}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground flex-shrink-0">
                      <span className={`w-1.5 h-1.5 rounded-full ${CAT_DOT[cat] || 'bg-gray-400'}`} />{cat}
                    </span>
                    <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${pos ? 'text-emerald-600' : 'text-rose-500'}`}>
                      {pos ? '+' : '-'}{Math.abs(t.coins).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT — Rewards */}
        <div ref={rewardsRef} className="col-span-12 lg:col-span-5 bg-card border border-border rounded-[20px] shadow-sm overflow-hidden" data-testid="right-panel">
          <div className="px-5 py-3.5 border-b border-border flex gap-5">
            <div className="flex items-center gap-1.5 text-sm font-medium pb-0.5 border-b-2 text-foreground" style={{ borderColor: '#0779BC' }}>
              <ShoppingBag className="w-4 h-4" /> Rewards
            </div>
          </div>
          <div className="p-4 max-h-[440px] overflow-y-auto space-y-4" data-testid="marketplace-panel">
            {rewards.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground"><ShoppingBag className="w-7 h-7 mx-auto mb-2 opacity-40" /><p className="text-sm">No rewards available</p></div>
            ) : cats.map(cat => (
              <div key={cat}>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">{cat}</p>
                {rewards.filter(r => r.category === cat).map(rw => {
                  const ok = bal >= rw.coins_cost;
                  const out = rw.stock === 0;
                  return (
                    <div key={rw.id}
                      className={`flex items-center gap-3 py-2.5 px-3 rounded-xl transition-all ${ok && !out ? 'hover:bg-accent/40 cursor-pointer' : ''} ${out ? 'opacity-25' : ''}`}
                      data-testid={`reward-card-${rw.id}`}
                      onClick={() => ok && !out && openRedeem(rw)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{rw.name}</p>
                        {!ok && !out && (
                          <p className="text-[10px] text-muted-foreground">Need {(rw.coins_cost - bal).toLocaleString()} more</p>
                        )}
                      </div>
                      <span className="text-sm font-bold text-foreground tabular-nums flex-shrink-0">{rw.coins_cost}</span>
                      {out ? (
                        <span className="text-[10px] text-rose-500 font-medium w-16 text-right">Sold out</span>
                      ) : (
                        <Button size="sm" className="h-7 text-xs rounded-lg px-3 w-16"
                          style={ok ? { backgroundColor: '#0779BC' } : undefined}
                          variant={ok ? 'default' : 'outline'} disabled={!ok}
                          onClick={(e) => { e.stopPropagation(); openRedeem(rw); }}
                          data-testid={`redeem-btn-${rw.id}`}
                        >{ok ? 'Redeem' : 'Locked'}</Button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ Redeem Modal ═══ */}
      <Dialog open={!!redeemModal} onOpenChange={(o) => !o && setRedeemModal(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="redeem-modal">
          <DialogHeader>
            <DialogTitle className="text-base">Confirm Redemption</DialogTitle>
            <DialogDescription>Deducts coins from your balance</DialogDescription>
          </DialogHeader>
          {redeemModal && (
            <div className="space-y-4">
              <div className="p-5 rounded-xl text-center bg-accent/50 border border-border">
                <p className="text-base font-semibold text-foreground">{redeemModal.name}</p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <Coins className="w-5 h-5 text-amber-500" />
                  <span className="text-3xl font-bold text-foreground">{redeemModal.coins_cost.toLocaleString()}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{curDisplay(redeemModal.coins_cost)}</p>
              </div>
              <div className="space-y-2 px-1">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Balance</span><span className="font-semibold tabular-nums">{bal.toLocaleString()}</span></div>
                <div className="h-px bg-border" />
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">After</span><span className="font-semibold tabular-nums" style={{ color: '#0779BC' }}>{(bal - redeemModal.coins_cost).toLocaleString()}</span></div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setRedeemModal(null)} className="flex-1" data-testid="redeem-cancel-btn">Cancel</Button>
                <Button className="flex-1" style={{ backgroundColor: '#0779BC' }} disabled={busy} onClick={() => handleRedeem(redeemModal)} data-testid="redeem-confirm-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}Confirm
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ Send Coins Modal — Bank Transfer Style ═══ */}
      <Dialog open={sendOpen} onOpenChange={(o) => { if (!o) setSendOpen(false); }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="send-coins-modal">
          <DialogTitle className="sr-only">Send Coins</DialogTitle>
          <AnimatePresence mode="wait">
            {/* ── STEP 1: Form ── */}
            {sendStep === 'form' && (
              <motion.div key="form" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#0779BC' }}>
                    <Send className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">Send Coins</h3>
                    <p className="text-xs text-muted-foreground">Transfer to a teammate</p>
                  </div>
                </div>

                {/* Available balance */}
                <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: 'rgba(7,121,188,0.04)', border: '1px solid rgba(7,121,188,0.1)' }}>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Available balance</p>
                  <p className="text-2xl font-bold text-foreground mt-1 tabular-nums">{bal.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">coins</span></p>
                </div>

                {/* Recipient search */}
                <div className="mb-4">
                  <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">To</Label>
                  <div className="relative">
                    {sendTo ? (
                      <div className="flex items-center justify-between h-10 px-3 rounded-lg border border-border bg-accent/30">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                            <User className="w-3 h-3 text-muted-foreground" />
                          </div>
                          <span className="text-sm font-medium">{sendTo.name}</span>
                        </div>
                        <button onClick={() => { setSendTo(null); setRecipientSearch(''); }} className="text-muted-foreground hover:text-foreground">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          className="pl-9"
                          placeholder="Search teammate..."
                          value={recipientSearch}
                          onChange={e => { setRecipientSearch(e.target.value); setShowRecipientList(true); }}
                          onFocus={() => setShowRecipientList(true)}
                          data-testid="send-recipient-search"
                        />
                        {showRecipientList && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {teammatesLoading ? (
                              <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" /></div>
                            ) : filteredTeammates.length === 0 ? (
                              <div className="p-3 text-center text-xs text-muted-foreground">No teammates found</div>
                            ) : (
                              filteredTeammates.map(t => (
                                <button
                                  key={t.user_id}
                                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-accent transition-colors text-left"
                                  onClick={() => { setSendTo(t); setShowRecipientList(false); setRecipientSearch(''); }}
                                  data-testid={`teammate-${t.user_id}`}
                                >
                                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                                  </div>
                                  <span className="font-medium">{t.name}</span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">Amount</Label>
                  <div className="relative">
                    <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                    <Input type="number" min="1" max={bal} placeholder="0" value={sendAmount}
                      onChange={e => setSendAmount(e.target.value)} className="pl-9 text-lg font-semibold" data-testid="send-amount-input" />
                  </div>
                  {sendAmount && parseInt(sendAmount) > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1 tabular-nums">{curDisplay(parseInt(sendAmount))}</p>
                  )}
                </div>

                {/* Message */}
                <div className="mb-5">
                  <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">Message (optional)</Label>
                  <Input placeholder="What's this for?" value={sendMsg} onChange={e => setSendMsg(e.target.value)} data-testid="send-message-input" />
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setSendOpen(false)} className="flex-1 h-11 rounded-xl" data-testid="send-cancel-btn">Cancel</Button>
                  <Button className="flex-1 h-11 rounded-xl text-sm font-semibold" style={{ backgroundColor: '#0779BC' }}
                    disabled={!sendTo || !sendAmount || parseInt(sendAmount) <= 0 || parseInt(sendAmount) > bal}
                    onClick={() => setSendStep('confirm')} data-testid="send-continue-btn"
                  >Continue</Button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 2: Confirm ── */}
            {sendStep === 'confirm' && (
              <motion.div key="confirm" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="p-6">
                <h3 className="text-base font-semibold text-foreground text-center mb-5">Confirm Transfer</h3>

                <div className="rounded-xl p-5 text-center mb-5" style={{ backgroundColor: 'rgba(7,121,188,0.04)', border: '1px solid rgba(7,121,188,0.1)' }}>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Sending</p>
                  <p className="text-4xl font-bold text-foreground tabular-nums">{parseInt(sendAmount).toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground mt-1">coins ({curDisplay(parseInt(sendAmount))})</p>
                </div>

                <div className="space-y-3 mb-5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">To</span>
                    <span className="font-semibold">{sendTo?.name}</span>
                  </div>
                  {sendMsg && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Message</span>
                      <span className="font-medium text-right max-w-[200px] truncate">{sendMsg}</span>
                    </div>
                  )}
                  <div className="h-px bg-border" />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Balance after</span>
                    <span className="font-semibold tabular-nums" style={{ color: '#0779BC' }}>{(bal - parseInt(sendAmount)).toLocaleString()} coins</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setSendStep('form')} className="flex-1 h-11 rounded-xl">Back</Button>
                  <Button className="flex-1 h-11 rounded-xl text-sm font-semibold" style={{ backgroundColor: '#0779BC' }}
                    disabled={sending} onClick={handleSend} data-testid="send-confirm-btn"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Send Now
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ── STEP 3: Success ── */}
            {sendStep === 'success' && sendResult && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-8 text-center relative overflow-hidden">
                {/* Confetti */}
                <div className="absolute inset-0 pointer-events-none">
                  {['#0779BC','#059669','#d97706','#7c3aed','#ef4444','#0779BC','#059669','#d97706'].map((c, i) => (
                    <ConfettiParticle key={i} delay={i * 0.08} x={(i - 4) * 12} color={c} />
                  ))}
                </div>

                {/* Check circle */}
                <motion.div
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.1 }}
                  className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center"
                  style={{ backgroundColor: '#059669' }}
                >
                  <Check className="w-10 h-10 text-white" strokeWidth={3} />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                  className="text-xl font-bold text-foreground"
                >Successfully Sent</motion.h3>

                <motion.p
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                  className="text-sm text-muted-foreground mt-2"
                >
                  You sent <span className="font-semibold text-foreground">{sendResult.coins.toLocaleString()} coins</span> to <span className="font-semibold text-foreground">{sendResult.recipient}</span>
                </motion.p>

                {sendResult.message && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
                    className="text-xs text-muted-foreground mt-1 italic">"{sendResult.message}"</motion.p>
                )}

                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                  className="mt-6 space-y-3">
                  <Button className="w-full h-12 rounded-xl text-sm font-semibold" style={{ backgroundColor: '#0779BC' }}
                    onClick={openSendDialog} data-testid="send-another-btn">
                    Send More Coins
                  </Button>
                  <Button variant="outline" className="w-full h-12 rounded-xl text-sm font-semibold"
                    onClick={() => setSendOpen(false)} data-testid="send-done-btn">
                    Back to Wallet
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default MyCoins;
